/**
 * RoutedTabs — ns-theme variant of Backstage's `RoutedTabs`.
 *
 * **Why not use `@backstage/core-components` RoutedTabs?**
 * Backstage's `RoutedTabs` renders the tab-strip and the routed content
 * pane as one combined block. Inside compound layouts like
 * `PageDetailLayout` (with separate `.Tabs` and `.Content` slots) that
 * combined block cannot be split across slots, so the tab-strip would end
 * up inside the content slot — breaking the spacing-ownership of
 * PageDetailLayout (tabs would render below the header rather than as
 * the page-subheader area).
 *
 * This implementation splits the same behaviour into two siblings — the
 * tab-strip (`<HeaderTabs>`) and the routed content (`useRoutes` result) —
 * so each can be placed in its own layout slot:
 *
 * ```tsx
 * const { tabStrip, content, selectedTitle } = useRoutedTabs(routes);
 *
 * <PageDetailLayout>
 *   <PageDetailLayout.Tabs>{tabStrip}</PageDetailLayout.Tabs>
 *   <PageDetailLayout.Content>
 *     <Helmet title={selectedTitle} />
 *     {content}
 *   </PageDetailLayout.Content>
 * </PageDetailLayout>
 * ```
 *
 * For the simple "tabs above content, no slot constraint" case, the
 * `<RoutedTabs>` component below renders both as a Fragment (same
 * behaviour as Backstage's built-in component).
 */

import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import { matchRoutes, useParams, useRoutes } from 'react-router-dom';
import { HeaderTabs, Link } from '@backstage/core-components';

/** @public — single sub-route consumed by `RoutedTabs` / `useRoutedTabs`. */
export interface RoutedTabsSubRoute {
  /** Content rendered when the tab is selected. */
  children: ReactElement;
  /** Path relative to the parent `<Route>` (e.g. `/`, `/docs`). */
  path: string;
  /** Human-readable label for the tab. */
  title: string;
}

/** @public — props for the compound `RoutedTabs` component. */
export interface RoutedTabsProps {
  /** Tabs in the order they should appear in the strip. */
  routes: ReadonlyArray<RoutedTabsSubRoute>;
}

/** @public — return value of the `useRoutedTabs` hook. */
export interface UseRoutedTabsResult {
  /** Routed content element — render in the content slot. */
  content: ReactNode;
  /** Zero-based index of the currently-selected tab. */
  selectedIndex: number;
  /** Title of the currently-selected tab (e.g. for `<Helmet>` titles). */
  selectedTitle: string | undefined;
  /** Tab-strip element — render in the tabs/sub-header slot. */
  tabStrip: ReactNode;
}

/**
 * Hook that powers `RoutedTabs`. Split out so consumers with slot-based
 * layouts (`PageDetailLayout.Tabs` + `.Content`) can place the tab-strip
 * and the routed content in different slots while sharing one routing
 * source of truth.
 *
 * @public
 */
export function useRoutedTabs(
  routes: ReadonlyArray<RoutedTabsSubRoute>,
): UseRoutedTabsResult {
  // Sort routes longest-path-first so `matchRoutes` prefers specific
  // subpaths over the catch-all root path. Matches Backstage's RoutedTabs.
  const sortedRouterRoutes = useMemo(
    () =>
      [...routes]
        .map(({ children, path }) => ({
          caseSensitive: false,
          element: children,
          path: `${path}/*`,
        }))
        .sort((a, b) =>
          b.path
            .replace(/\/\*$/, '')
            .localeCompare(a.path.replace(/\/\*$/, '')),
        ),
    [routes],
  );

  const content = useRoutes(sortedRouterRoutes);

  const params = useParams();
  let currentRoute = params['*'] ?? '';
  if (!currentRoute.startsWith('/')) {
    currentRoute = `/${currentRoute}`;
  }
  const [matchedRoute] = matchRoutes(sortedRouterRoutes, currentRoute) ?? [];
  // Map the matched route's path back to the ORIGINAL `routes` order so the
  // tab-strip highlights the correct tab (sortedRouterRoutes appends `/*` and
  // is sorted longest-first, so its index does not match `routes`).
  const matchedOriginalPath = matchedRoute
    ? matchedRoute.route.path.replace(/\/\*$/, '')
    : undefined;
  const foundIndex =
    matchedOriginalPath === undefined
      ? 0
      : routes.findIndex(r => r.path === matchedOriginalPath);
  const selectedIndex = foundIndex === -1 ? 0 : foundIndex;
  const selectedTitle = routes[selectedIndex]?.title;

  const headerTabs = useMemo(
    () =>
      routes.map(t => {
        let to = t.path;
        to = to.replace(/\/\*$/, '');
        to = to.replace(/^\//, '');
        return {
          id: t.path,
          label: t.title,
          tabProps: { component: Link, to },
        };
      }),
    [routes],
  );

  const tabStrip =
    headerTabs.length > 0 ? (
      <HeaderTabs selectedIndex={selectedIndex} tabs={headerTabs} />
    ) : null;

  return { content, selectedIndex, selectedTitle, tabStrip };
}

/**
 * Compound component that renders the tab-strip and the routed content
 * as siblings inside a Fragment. Behaviourally equivalent to Backstage's
 * `@backstage/core-components` `RoutedTabs`, but consumers that need
 * separate slots should call `useRoutedTabs` directly.
 *
 * @public
 */
export const RoutedTabs = (props: RoutedTabsProps): ReactElement => {
  const { content, tabStrip } = useRoutedTabs(props.routes);
  return (
    <>
      {tabStrip}
      {content}
    </>
  );
};
